import { inject, Injector, runInInjectionContext } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { AuthSessionStore } from '../+state';
import { navigateOrReport } from '../util/navigate-or-report.util';

/**
 * Resolves whether the authenticated user has write access to the entity
 * addressed by a route. Returning `null` is treated as "entity not found".
 *
 * Implementations read a backend-provided `can_edit` flag from the entity
 * payload loaded by the relevant domain store. No `company_id` comparison
 * should occur here — the backend is the sole authority on entity writability
 * (see `docs/adr/010-no-frontend-company-id-comparisons.md`).
 */
export type EntityWritabilityResolver = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => Observable<boolean | null>;

/**
 * Guard factory enforcing entity write access, keyed on a backend-provided
 * `can_edit` flag rather than a frontend `company_id` comparison.
 *
 * Per `15-product-invariants.mdc` §6 and
 * `docs/adr/010-no-frontend-company-id-comparisons.md`, ownership decisions
 * MUST come from the backend. Pass a domain-specific
 * {@link EntityWritabilityResolver} that reads the entity's `can_edit` flag
 * (or equivalent) from the loaded payload.
 *
 * Admin users always pass. Unauthenticated requests are expected to be
 * handled upstream by {@link authGuard}.
 *
 * @param resolveWritability - Strategy that produces `true` (user may edit),
 *   `false` (user may not edit), or `null` (entity not found). Both `null`
 *   and `false` block activation and redirect to `/`.
 * @returns A `CanActivateFn` that resolves to `true` when the user has write
 *   access or is an `admin`, `false` otherwise.
 * @example
 * import { Routes } from '@angular/router';
 * import {
 *   authGuard,
 *   entityEditableGuard,
 * } from '@indutrust/auth-data';
 *
 * // Domain feature routes pass an EntityWritabilityResolver that GETs the
 * // entity and reads can_edit. Omitted can_edit is treated as writable
 * // until the live API ships it.
 * export const routes: Routes = [
 *   {
 *     path: ':id/edit',
 *     canActivate: [
 *       authGuard,
 *       entityEditableGuard(factoryWritabilityResolver),
 *     ],
 *     loadChildren: async () =>
 *       import('@indutrust/factory-feature').then((m) => m.libFactoryRoutes),
 *   },
 * ];
 */
export function entityEditableGuard(
  resolveWritability: EntityWritabilityResolver
): CanActivateFn {
  return (route, state) => {
    const authStore = inject(AuthSessionStore);
    const injector = inject(Injector);
    const router = inject(Router);

    return toObservable(authStore.authenticationInitialized).pipe(
      filter((initialized): boolean => initialized),
      first(),
      switchMap(() => {
        if (authStore.isAdmin()) {
          return of(true);
        }
        if (!authStore.user()) {
          navigateOrReport(router, ['/'], 'Navigation to home failed');
          return of(false);
        }
        // Resolvers often call `inject()` — must run in an injection context
        // (switchMap is not one).
        return runInInjectionContext(injector, () =>
          resolveWritability(route, state)
        ).pipe(
          first(),
          map((canEdit) => {
            if (canEdit !== true) {
              navigateOrReport(router, ['/'], 'Navigation to home failed');
              return false;
            }
            return true;
          })
        );
      })
    );
  };
}
